import { defineStore } from 'pinia';
import {useDeviceData} from "~/utils/fingerprint/deviceData";
import {jwtDecode} from "~/utils/jwtDecode";
import type {TUserRole, TUserProfile} from "~/types/user";

const AUTH_SESSION_KEY = 'authSession';
const LEGACY_TOKEN_KEY = 'accessToken';

export const DOCTOR_WIDGET_PREFIX = '/widget/doctor/';

export const isDoctorWidgetContext = () =>
    import.meta.client && window.location.pathname.startsWith(DOCTOR_WIDGET_PREFIX);

// В iframe виджета на сайте клиники httpOnly-cookie — third-party и браузером
// не отправляется, поэтому там остаётся токен-канал: localStorage + заголовок AuthToken.
// Ссылка виджета врача открывается отдельной вкладкой на нашем домене, cookie там работает.
export const isWidgetContext = () =>
    import.meta.client && window.location.pathname.startsWith('/widget') && !isDoctorWidgetContext();

export const useAuthStore = defineStore('authStore', {
    state: () => ({
        _fingerprint: <string | null> null,
        _accessToken: <string | null> null,   // заполняется только в widget-контексте
        _sessionExp: <number | null> null,    // unix-секунды; основной сайт (cookie-auth)
        _userRole: <TUserRole> null,
        _userProfileData: <any> null
    }),
    getters: {
        fingerprint: state => state._fingerprint,
        accessToken: state => state._accessToken,
        sessionExp: state => state._sessionExp,
        isAuth: state => !!state._accessToken || !!state._sessionExp,
        userRole: state => state._userRole,
        userProfileData: state => state._userProfileData
    },
    actions: {
        getFingerprint() {
            if(!this._fingerprint) {
                const {getFingerprintHash} = useDeviceData();
                this._fingerprint = getFingerprintHash();
            }
        },
        setAccessToken(value: string | null) {
            if (isWidgetContext()) {
                this._accessToken = value;
                if(value) {
                    localStorage.setItem(LEGACY_TOKEN_KEY, value);
                } else {
                    localStorage.removeItem(LEGACY_TOKEN_KEY);
                }
                return;
            }

            // Основной сайт: токен в JS не храним — аутентификация идёт по
            // httpOnly-cookie (credentials: 'include' в useApi). Персистим только
            // exp и роль, чтобы сессия переживала перезагрузку страницы.
            this._accessToken = null;
            if (import.meta.client) {
                localStorage.removeItem(LEGACY_TOKEN_KEY);
            }
            if (value) {
                let exp: number | null = null;
                let role: TUserRole = null;
                try {
                    const payload = jwtDecode(value);
                    exp = typeof payload?.exp === 'number' ? payload.exp : null;
                    role = payload?.role ?? null;
                } catch {}
                this._sessionExp = exp;
                if (role) this._userRole = role;
                if (import.meta.client) {
                    localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify({exp, role: role ?? this._userRole}));
                }
            } else {
                this._sessionExp = null;
                this._userRole = null;
                if (import.meta.client) {
                    localStorage.removeItem(AUTH_SESSION_KEY);
                }
            }
        },
        setSessionExp(exp: number | null) {
            this._sessionExp = exp;
            if (import.meta.client && !isWidgetContext()) {
                if (exp) {
                    localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify({exp, role: this._userRole}));
                } else {
                    localStorage.removeItem(AUTH_SESSION_KEY);
                }
            }
        },
        restoreSession() {
            if (!import.meta.client || isWidgetContext()) return;
            const raw = localStorage.getItem(AUTH_SESSION_KEY);
            if (!raw) return;
            try {
                const {exp, role} = JSON.parse(raw) as {exp?: number; role?: TUserRole};
                const expSeconds = typeof exp === 'number' ? exp : null;
                if (!expSeconds || expSeconds * 1000 <= Date.now()) {
                    localStorage.removeItem(AUTH_SESSION_KEY);
                    return;
                }
                this._sessionExp = expSeconds;
                if (role) {
                    this._userRole = role;
                }
            } catch {
                localStorage.removeItem(AUTH_SESSION_KEY);
            }
        },
        setUserRole(value: TUserRole) {
            this._userRole = value;
        },
        setUserProfileData(value: any) {
            this._userProfileData = value;
        }
    }
})
